/**
 * "Which one is this?" - the state and copy for placing an unmatched ad.
 *
 * Kept out of the component because it is the part worth asserting: there is no
 * testing-library in this project, so anything inside a `.tsx` is only reachable
 * from the browser suite. The same split as `whopEmbed.ts`.
 *
 * This screen is the road most ads take, not an error path. Of 755 real ads the
 * deterministic matcher placed none and the LLM placed roughly a third - only
 * the ones whose name already contains the card's name. The rest cannot be
 * placed from a name at all, so a person looks at the thumbnail and says which
 * card it is. The copy has to read like a normal piece of work, not like a
 * failure being reported.
 *
 * @module web/screens/app/unmatchedAds
 */
import { fmtEur, MONEY_LOCALE } from "../../money.ts";

export interface UnmatchedAd {
  metaAdId: string;
  adName: string;
  thumbnailUrl: string | null;
  spendCents: number;
  impressions: number;
}

/**
 * Euro, with cents, as the rest of the portal shows money.
 *
 * It did not, until now: this was the only `de-DE` formatter in the product,
 * so one screen said "144,11 €" while the wallet, billing, projects and the
 * brief all said "€144.11". `fmtEur` is the single formatter; there is no
 * second opinion about what money looks like.
 */
export function formatSpend(cents: number): string {
  return fmtEur(cents ?? 0);
}

/** Thousands separators from the same locale as the money beside them. */
export function formatCount(n: number): string {
  return (n ?? 0).toLocaleString(MONEY_LOCALE);
}

/**
 * Drop an ad once it has been placed.
 *
 * Returning a new array rather than splicing, because React decides what to
 * redraw by identity - a mutated array is the same array and the row stays on
 * screen after the client has dealt with it.
 */
export function withoutAd(ads: UnmatchedAd[], metaAdId: string): UnmatchedAd[] {
  return ads.filter(a => a.metaAdId !== metaAdId);
}

export type PanelState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "empty" }
  | { kind: "list"; ads: UnmatchedAd[]; heading: string };

/**
 * What the panel is doing right now.
 *
 * `empty` is a real, good state - every ad that has spent money is placed - and
 * it says so. An empty list rendered as an empty list reads as "broken", which
 * is the same complaint §13 records against every other silent-nothing in this
 * codebase.
 */
export function panelState(
  loading: boolean, error: string | null, ads: UnmatchedAd[],
): PanelState {
  if (loading) return { kind: "loading" };
  if (error) return { kind: "error", message: error };
  if (!ads.length) return { kind: "empty" };
  return {
    kind: "list",
    ads,
    heading: ads.length === 1 ? "1 ad needs placing" : `${ads.length} ads need placing`,
  };
}

/**
 * Whether "Link" should be pressable.
 *
 * Deliberately only "did they type anything". The rule for what counts as a
 * Trello card link lives on the server, in `meta-card-link.ts`, and stays there:
 * a second copy here would be one more thing to drift, and a client-side rule
 * that is stricter than the server's silently refuses links that would have
 * worked. The server's refusal message is the feedback.
 */
export function canSubmit(pasted: string, busy: boolean): boolean {
  return !busy && pasted.trim().length > 0;
}
